import React, { useCallback } from 'react';

import { useGlobalSearchParams, useRouter } from 'expo-router';
import { ICartEntry } from 'features/cart/types';
import { useBestPromotion } from 'features/promotions/hooks/use-best-promotion';
import { PromoMessageContent } from 'features/promotions/types';
import { useStoreContext } from 'features/store';
import { LayoutChangeEvent, StyleSheet, Text, View } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { useAuthRequiredInModal } from 'pages/authentication/components/auth-required';
import { ActivatablePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/activatable-promo-ribbon';
import { SimplePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/simple-promo-ribbon';
import { ModalAuthTrigger } from 'state/auth/types';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useUIContext } from 'state/ui';
import { ZIndex, primitive } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';

export const PromoMessageRibbon = ({
  cartEntries,
  rounded = false,
  skipStoreDayPartHours = false,
}: {
  cartEntries?: ILegacyCartEntry[] | ICartEntry[];
  rounded?: boolean;
  skipStoreDayPartHours?: boolean;
}) => {
  const { activatePromo } = useGlobalSearchParams<{ activatePromo?: string }>();
  const router = useRouter();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;
  const { store } = useStoreContext();
  const autoActivate = activatePromo === 'true';

  useAuthRequiredInModal({
    skip: !loyaltyEnabled || !autoActivate,
    trigger: ModalAuthTrigger.Global,
    // Prevent the modal from being displayed again on page refresh after user closes it
    onModalClose: () => router.setParams({ activatePromo: '' }),
  });

  const { promoMessageHeightRef } = useUIContext();

  const clearActivateParam = useCallback(() => {
    // Clear the activatePromo param once the promotion is already activated or
    // doesn't require activation.
    if (activatePromo) {
      router.setParams({ activatePromo: '' });
    }
  }, [activatePromo, router]);

  const {
    promotionId,
    mustActivate,
    userActivatedAt,
    content,
    refetchPromotion,
  } = useBestPromotion({
    cartEntries,
    storeId: store?.number ?? undefined,
    skipStoreDayPartHours,
    onActivateResolved: clearActivateParam,
  });

  const handleActivated = useCallback(async () => {
    // Refetch every active observer of the underlying promotion query (each has
    // its own variables / cache key). A contextual ribbon and the homepage
    // ribbon can share the same promo but use different inputs.
    await refetchPromotion();
    if (autoActivate) {
      router.setParams({ activatePromo: '' });
    }
  }, [refetchPromotion, autoActivate, router]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      promoMessageHeightRef.current = event.nativeEvent.layout.height;
    },
    [promoMessageHeightRef]
  );

  const message = mustActivate
    ? renderRibbonMessage({ content, userActivatedAt, mustActivate })
    : renderRibbonMessage({ content });

  if (!message) {
    promoMessageHeightRef.current = 0;
    return null;
  }

  return (
    <View onLayout={handleLayout}>
      {mustActivate ? (
        <ActivatablePromoRibbon
          promotionMeta={{ name: content?.name, loyaltyEngineId: content?.loyaltyEngineId }}
          message={message}
          loyaltyId={loyaltyId!}
          promotionId={promotionId!}
          userActivatedAt={userActivatedAt}
          onActivated={handleActivated}
          autoActivate={autoActivate}
          rounded={rounded}
        />
      ) : (
        <SimplePromoRibbon message={message} rounded={rounded} />
      )}
    </View>
  );
};

function renderRibbonMessage({
  userActivatedAt,
  content,
  mustActivate,
}: {
  userActivatedAt?: string | null;
  content?: PromoMessageContent | null;
  mustActivate?: boolean | null;
}): React.ReactElement | null {
  const promoMessage = content?.promoMessage;
  const blockContent = userActivatedAt
    ? content?.activatedPromoMessageBlock
    : content?.promoMessageBlock;

  if (blockContent) {
    return (
      <SanityBlockRenderer
        content={blockContent}
        style={styles.blockRendererWrapper}
        textProps={{
          color: primitive.$bbqBrown,
          textAlign: mustActivate ? 'left' : 'center',
          fontSize: fontSizes.xs,
          lineHeight: fontSizes.md,
        }}
      />
    );
  }

  if (promoMessage) {
    return (
      <Text style={[styles.ribbonText, mustActivate && styles.activablePromotionText]}>
        {promoMessage}
      </Text>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  blockRendererWrapper: {
    zIndex: ZIndex.MAX,
    flex: 1,
  },
  ribbonText: {
    fontFamily: 'Flame-Regular',
    fontSize: fontSizes.xs,
    color: primitive.$bbqBrown,
    textAlign: 'center',
    zIndex: ZIndex.MAX,
  },
  activablePromotionText: {
    textAlign: 'left',
  },
});
